import {
  type ControllerProps,
  type Command,
  type CommandCategory,
  type CommandListComponentProps,
  type SlashCommandType,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/slash-command'
import { tabContentId } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/tab-content/wrapper'
import { Extension } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import { ReactRenderer } from '@tiptap/react'
import { Suggestion } from '@tiptap/suggestion'
import * as React from 'react'
import tippy, { type Instance, type Props } from 'tippy.js'
import { create } from 'zustand'
import { queryMatches } from '~/lib/simple-input-search'

export const SlashCommands = Extension.create<{
  component: React.FC<CommandListComponentProps> | null
  suggestion: {
    char: string
    startOfLine?: boolean
    pluginKey: string
  }
  onOpen: () => void
  onClose: () => void
  onSelectBlock: SlashCommandType['onSelectBlock']
}>({
  name: 'slash-command',

  addOptions() {
    return {
      component: null,
      suggestion: {
        char: '/',
        pluginKey: 'suggestion-plugin',
      },
      onOpen: () => undefined,
      onClose: () => undefined,
      onSelectBlock: () => Promise.resolve(undefined),
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
        pluginKey: new PluginKey(this.options.suggestion.pluginKey),
        items: ({ editor: _, query }) => {
          const { commands } = useSlashCommandStore.getState()

          if (!commands) return []

          const filteredCommands: CommandCategory[] = []

          commands.forEach((c) => {
            const nameMatches = queryMatches(query, c.name)

            if (nameMatches) return filteredCommands.push(c)

            const _filteredCommands = c.commands.filter((c) => {
              const titleMatch = queryMatches(query, c.title)
              if (titleMatch) return true

              const words = c.title.split(' ')

              const wordsMatch = words.some((w) => queryMatches(query, w))

              if (wordsMatch) return true

              const keywordMatch = c.keywords?.some((k) => {
                return queryMatches(query, k)
              })

              return keywordMatch
            })

            if (_filteredCommands.length < 1) return

            filteredCommands.push({
              ...c,
              commands: _filteredCommands,
            })
          })

          return filteredCommands
        },
        command: ({ editor, range, props }) => {
          const command = props as unknown as Command
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          command.command({
            editor,
            range,
            onExit: (props as any).onExit,
            onSelectBlock: this.options.onSelectBlock,
          })
        },
        render: () => {
          let component: ReactRenderer<{
            onKeyDown: (args: { event: KeyboardEvent }) => boolean
          }>

          let popup: Instance<Props>
          let isOpen = false
          const onExitBase = (onCloseCallback: () => void) => {
            if (!isOpen) return
            isOpen = false
            popup?.destroy()
            component?.destroy()
            onCloseCallback()
            this.editor.view.dom.classList.remove('slash-command-open')
          }

          return {
            onStart: (props) => {
              if (!props.editor.isFocused) {
                return
              }

              isOpen = true
              this.options.onOpen()
              this.editor.view.dom.classList.add('slash-command-open')
              component = new ReactRenderer(CommandsListController, {
                editor: props.editor,
                props: {
                  ...props,
                  component: this.options.component,
                  onExit: () => {
                    onExitBase(this.options.onClose)
                  },
                },
              })

              const tabContentWrapper = document.querySelector<HTMLDivElement>(
                `#${tabContentId}`
              )
              if (!tabContentWrapper) {
                throw new Error('tabContentWrapper is null or undefined')
              }

              popup = tippy(tabContentWrapper, {
                getReferenceClientRect: () => {
                  const clientRect = props.clientRect?.()
                  if (!clientRect) {
                    throw new Error('clientRect is null or undefined')
                  }
                  return clientRect
                },
                appendTo: tabContentWrapper,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
              })
            },
            onUpdate: (props) => {
              component.updateProps({
                ...props,
                component: this.options.component,
              })

              popup?.setProps({
                getReferenceClientRect: () => {
                  const clientRect = props.clientRect?.()
                  if (!clientRect) {
                    throw new Error('clientRect is null or undefined')
                  }
                  return clientRect
                },
              })
            },
            onKeyDown(props) {
              if (!isOpen) return false

              const handled = component?.ref?.onKeyDown?.(props)

              // If the component handled it, stop propagation
              if (handled) {
                return true
              }

              return false
            },
            onExit: () => {
              onExitBase(this.options.onClose)
            },
          }
        },
      }),
    ]
  },
})

class CommandsListController extends React.Component<
  ControllerProps,
  { selectedIndex: number }
> {
  constructor() {
    // @ts-ignore
    super()
    this.state = {
      selectedIndex: 0,
    }
    this.selectItem = this.selectItem.bind(this)
  }

  onKeyDown({ event }: { event: KeyboardEvent }) {
    if (event.key === 'ArrowUp') {
      // dont stop propagation here plz
      return true
    }

    if (event.key === 'ArrowDown') {
      // dont stop propagation here plz
      return true
    }

    if (event.key === 'Enter') {
      // dont stop propagation here plz
      return true
    }

    if (event.key === 'Escape') {
      // dont stop propagation here plz
      return true
    }

    return false
  }

  selectItem(index: number) {
    const flatItems = this.props.items.flatMap((c) => c.commands)
    const item = flatItems[index]

    if (item) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.props.command({ ...(item as any), onExit: this.props.onExit })
    }
  }

  componentDidUpdate(prevProps: ControllerProps) {
    const prevFatItems = prevProps.items.flatMap((c) => c.commands)
    const flatItems = this.props.items.flatMap((c) => c.commands)
    if (prevFatItems.length !== flatItems.length) {
      this.setState({ selectedIndex: 0 })
    }
  }

  render() {
    const { items, component: Component } = this.props
    const { selectedIndex } = this.state

    const setSelectedIndex = (index: number) => {
      this.setState({ selectedIndex: index })
    }

    return (
      <Component
        setMouseInteracting={() => {}}
        items={items}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        selectItem={this.selectItem}
        onExit={this.props.onExit}
      />
    )
  }
}

// we create a store to perform "surgical" updates
export const useSlashCommandStore = create<{
  commands: CommandCategory[] | null
}>(() => {
  return { commands: null }
})
export { CommandListComponentProps }
